import { useQuery, useQueryClient } from '@tanstack/react-query';
import Grid from '@mui/material/Grid';
import TextHoverCopy from '../../../shared/text-hover-copy';
import { useEffect, useState } from 'react';
import { useUserSettings } from '../../../utils/use-user-settings';
import { User } from '../../../../types';
import {
  getAllDecomModels,
  getDomainsQuery,
  getUserDecomStatus,
  getUserQuery,
  getUserStorageDetails,
  UserKeys,
} from '../../../api/users-api';
import { Messages } from '../../../utils/message-constants';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
  ToolsRoles,
  UserRoles,
} from '../../../utils/roles/client-roles';
import UserAvatar from '../../../shared/user-avatar';
import UserReportsMenu from './user-reports-menu';
import DeleteUserDialog from './delete-user-dialog';
import EditProfilePicDialog from './edit-profile-pic-dialog';
import PauseUserDecomDialog from './decom-actions/pause-user-from-decom-dialog';
import UnpauseUserDecomDialog from './decom-actions/unpause-user-from-decom-dialog';
import TerminateUserDecomDialog from './decom-actions/terminate-user-from-decom-dialog';
import PurgeUserDecomDialog from './decom-actions/purge-user-from-decom-dialog';
import ReportsDialog from '../reports-dialog';
import axios from 'axios';
import { useStore } from '../../state/zustand-store';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import StatusLabel from '../../../shared/status-label';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import EditProfilePicDialogOld from './edit-profile-pic-dialog-old';

import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import { useTheme, styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import Assessment from '@mui/icons-material/Assessment';
import Delete from '@mui/icons-material/Delete';
import Check from '@mui/icons-material/Check';
import CameraAlt from '@mui/icons-material/CameraAlt';
import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import DeleteForever from '@mui/icons-material/DeleteForever';
import Cancel from '@mui/icons-material/Cancel';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CalendarToday from '@mui/icons-material/CalendarToday';
import AccessTime from '@mui/icons-material/AccessTime';
import Mail from '@mui/icons-material/Mail';
import Storage from '@mui/icons-material/Storage';
import Groups from '@mui/icons-material/Groups';
import Security from '@mui/icons-material/Security';

interface UserDetailsCardProps {
  detailsExpanded: boolean;
  setDetailsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail: string;
}

export default function UserDetailsCard({
  detailsExpanded,
  setDetailsExpanded,
  userEmail,
}: UserDetailsCardProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const {
    primaryDomain,
    userRoles,
    changeLogRoles,
    toolsRoles,
    directoryRoles,
    currentTier,
    features,
  } = useUserSettings();
  const uiEnhancementFeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );

  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [anchorDecomEl, setAnchorDecomEl] = useState<null | HTMLElement>(null);
  const openDecom = Boolean(anchorDecomEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDecomClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorDecomEl(event.currentTarget);
  };
  const handleDecomClose = () => {
    setAnchorDecomEl(null);
  };

  const [openReportsDialog, setOpenReportsDialog] = useState(false);
  const [openEditPictureDialog, setOpenEditPictureDialog] = useState(false);
  const [openPauseUserDecomDialog, setOpenPauseUserDecomDialog] =
    useState(false);
  const [openUnpauseUserDecomDialog, setOpenUnpauseUserDecomDialog] =
    useState(false);
  const [openTerminateUserDecomDialog, setOpenTerminateUserDecomDialog] =
    useState(false);
  const [openPurgeUserDecomDialog, setOpenPurgeUserDecomDialog] =
    useState(false);
  const [selectedReport, setSelectedReport] = useState<{
    displayName: string;
    reportType: string;
    role: string;
  } | null>(null);

  const {
    isLoading: isUserLoading,
    data: user,
    error,
  } = useQuery(getUserQuery(userEmail));
  useEffect(() => {
    if (!error) return;
    if (axios.isAxiosError(error)) {
      setSnackbar({
        open: true,
        severity: 'info',
        message: `User wasn't found in Google so we've removed them from gPanel indexing.`,
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserList],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UsersIndexedPage],
      });
      navigate(`/${pagesConfig.users.link}`);
    }
  }, [error]);

  const theme = useTheme();

  const { data: domains } = useQuery(getDomainsQuery());

  const { data: userDecomStatus, isError: isUserDecomStatusError } = useQuery({
    queryKey: [UserKeys.DecomStatus, userEmail],
    queryFn: () => getUserDecomStatus(userEmail),
    staleTime: 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('user decommission status'),
    },
  });

  const { data: decomModels } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
  });

  const isMissingDecomModel =
    !userDecomStatus?.decomModelKeyString ||
    !decomModels?.find((d) => d.id === userDecomStatus.decomModelKeyString);

  const {
    data: userWithStorageDetails,
    isLoading: isUserStorageLoading,
    error: storageError,
  } = useQuery({
    queryKey: [UserKeys.UserStorage, userEmail],
    queryFn: async () => {
      try {
        return await getUserStorageDetails(userEmail);
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.data?.exceptionType === 'DriveStorageUnavailable'
        ) {
          throw new Error('DriveStorageUnavailable');
        }
        throw error;
      }
    },
  });

  function convertBytes(bytes: number) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let unitIndex = 0;

    while (bytes >= 1024 && unitIndex < units.length - 1) {
      bytes /= 1024;
      unitIndex++;
    }

    return Math.round(bytes).toLocaleString() + ' ' + units[unitIndex];
  }

  function createStatusChip(user: User) {
    const status = user.isArchived
      ? 'ARCHIVED'
      : user.suspended
      ? 'SUSPENDED'
      : 'ACTIVE';
    return <StatusLabel status={status} />;
  }

  function createOU(user: User) {
    if (user.orgUnitPath === '/') {
      return primaryDomain;
    } else {
      return user.orgUnitPath.split('/').slice(-1)[0];
    }
  }

  const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 25,
    height: 25,
    border: `2px solid ${theme.palette.background.paper}`,
  }));

  return (
    <>
      <Stack
        sx={{
          border:
            theme.palette.mode === 'dark'
              ? `0.5px solid ${theme.palette.semantic.outline.medium}`
              : undefined,
          borderRadius: '4px',
          boxShadow: '0 1px 6px 0 #0000001F',
          backgroundColor: (theme) => theme.palette.background.paper,
          cursor: 'pointer',
          overflowX: 'hidden',
        }}
        onClick={() => setDetailsExpanded((prev) => !prev)}
      >
        <Grid
          container
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            px: 1,
            pt: 1,
            flexWrap: 'nowrap',
            minWidth: 0,
            overflowX: 'hidden',
          }}
        >
          <Grid
            sx={{
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              {isUserLoading ? (
                <Skeleton variant="circular" width={48} height={48} />
              ) : (
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.ChangeProfilePicture.value
                    ) ? (
                      <SmallAvatar
                        alt={user?.name?.fullName || ''}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenEditPictureDialog(true);
                        }}
                        sx={{
                          ':hover': {
                            cursor: 'pointer',
                            bgcolor: (theme) => theme.palette.grey[600],
                            color: 'white',
                          },
                          width: 20,
                          height: 20,
                          fontSize: 12,
                          color: 'black',
                        }}
                      >
                        <CameraAlt sx={{ width: 12 }} />
                      </SmallAvatar>
                    ) : null
                  }
                  sx={{ flexShrink: 0 }}
                >
                  <UserAvatar
                    userEmail={userEmail}
                    sx={{ width: 48, height: 48 }}
                  />
                </Badge>
              )}

              <Stack sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {isUserLoading ? (
                    <Skeleton variant="rounded" width={200} height={25} />
                  ) : user?.name ? (
                    <TextHoverCopy
                      clipboardValue={`${user?.name?.givenName || ''} ${
                        user?.name?.familyName || ''
                      }`}
                      value={
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: 'bold',
                            fontSize: 20,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`${user?.name?.givenName || ''} ${
                            user?.name?.familyName || ''
                          }`}
                        </Typography>
                      }
                      valueType="Full Name"
                    />
                  ) : null}

                  {isUserLoading ? (
                    <Skeleton variant="rounded" width={69} height={24} />
                  ) : user ? (
                    createStatusChip(user)
                  ) : null}

                  {userDecomStatus && !isUserDecomStatusError && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Chip
                        clickable
                        onClick={handleDecomClick}
                        variant="outlined"
                        icon={
                          userDecomStatus.decomComplete ? (
                            <Check />
                          ) : userDecomStatus.isTerminated ? (
                            <Cancel />
                          ) : userDecomStatus.isactive ? (
                            <PlayArrow />
                          ) : (
                            <Pause />
                          )
                        }
                        label={
                          userDecomStatus.decomComplete
                            ? 'Finished!'
                            : userDecomStatus.isTerminated
                            ? 'Terminated'
                            : userDecomStatus.isactive
                            ? 'Processing...'
                            : 'Paused'
                        }
                        color={
                          userDecomStatus.decomComplete
                            ? 'success'
                            : userDecomStatus.isTerminated
                            ? 'error'
                            : userDecomStatus.isactive
                            ? 'info'
                            : 'warning'
                        }
                        size="small"
                      />

                      <Menu
                        anchorEl={anchorDecomEl}
                        open={openDecom}
                        onClose={handleDecomClose}
                        sx={{ color: theme.palette.text.primary, py: 0 }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            pb: 2,
                            pt: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="body1"
                            color={theme.palette.text.primary}
                          >
                            Status Actions
                          </Typography>
                        </Box>
                        {userDecomStatus.isactive &&
                          !userDecomStatus.decomComplete &&
                          !userDecomStatus.isTerminated && (
                            <Tooltip
                              arrow
                              title={
                                isMissingDecomModel
                                  ? 'Cannot pause: this user has no associated decom policy.'
                                  : ''
                              }
                            >
                              <span>
                                <MenuItem
                                  disabled={
                                    !isAbleToSeeUIElement(
                                      toolsRoles,
                                      ToolsRoles.PauseDecomForUser.value
                                    ) || isMissingDecomModel
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenPauseUserDecomDialog(true);
                                    handleDecomClose();
                                  }}
                                  sx={{ py: 1, pl: 3, pr: 4 }}
                                >
                                  <Pause
                                    fontSize="inherit"
                                    sx={{
                                      mr: 2,
                                      color: theme.palette.action.active,
                                    }}
                                  />{' '}
                                  Pause
                                </MenuItem>
                              </span>
                            </Tooltip>
                          )}

                        {!userDecomStatus.isactive &&
                          !userDecomStatus.decomComplete &&
                          !userDecomStatus.isTerminated && (
                            <Tooltip
                              arrow
                              title={
                                isMissingDecomModel
                                  ? 'Cannot unpause: this user has no associated decom policy.'
                                  : ''
                              }
                            >
                              <span>
                                <MenuItem
                                  disabled={
                                    !isAbleToSeeUIElement(
                                      toolsRoles,
                                      ToolsRoles.UnpauseDecomForUser.value
                                    ) || isMissingDecomModel
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenUnpauseUserDecomDialog(true);
                                    handleDecomClose();
                                  }}
                                  sx={{ py: 1, pl: 3, pr: 4 }}
                                >
                                  <PlayArrow
                                    fontSize="small"
                                    sx={{
                                      mr: 2,
                                      color: theme.palette.action.active,
                                    }}
                                  />{' '}
                                  Unpause
                                </MenuItem>
                              </span>
                            </Tooltip>
                          )}

                        {!userDecomStatus.isTerminated &&
                          !userDecomStatus.decomComplete && (
                            <MenuItem
                              disabled={
                                !isAbleToSeeUIElement(
                                  toolsRoles,
                                  ToolsRoles.TerminateDecomForUser.value
                                )
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenTerminateUserDecomDialog(true);
                                handleDecomClose();
                              }}
                              sx={{ py: 1, pl: 3, pr: 4 }}
                            >
                              <Cancel
                                fontSize="small"
                                sx={{
                                  mr: 2,
                                  color: theme.palette.action.active,
                                }}
                              />{' '}
                              Terminate
                            </MenuItem>
                          )}

                        <MenuItem
                          disabled={
                            !isAbleToSeeUIElement(
                              toolsRoles,
                              ToolsRoles.PurgeDecomForUser.value
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenPurgeUserDecomDialog(true);
                            handleDecomClose();
                          }}
                          sx={{ py: 1, px: 3 }}
                        >
                          <DeleteForever
                            fontSize="small"
                            sx={{ mr: 2, color: theme.palette.action.active }}
                          />{' '}
                          Purge
                        </MenuItem>
                      </Menu>
                    </Stack>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: 'wrap', pb: 1 }}
                >
                  {isUserLoading ? (
                    <Skeleton variant="rounded" width={200} height={20} />
                  ) : user?.primaryEmail ? (
                    <TextHoverCopy
                      clipboardValue={user.primaryEmail}
                      value={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color:
                              theme.palette.mode === 'light'
                                ? theme.palette.grey[700]
                                : theme.palette.grey[400],
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minHeight: '24px',
                          }}
                        >
                          <Mail fontSize="inherit" sx={{ mr: 1 }} />
                          {user.primaryEmail}
                        </Box>
                      }
                      valueType="Email Address"
                      iconSx={{ fontSize: 14 }}
                    />
                  ) : null}

                  <Typography
                    sx={{
                      fontSize: 14,
                      color:
                        theme.palette.mode === 'light'
                          ? theme.palette.grey[700]
                          : theme.palette.grey[400],
                    }}
                  >
                    •
                  </Typography>

                  {isUserLoading ? (
                    <Skeleton variant="rounded" width={100} height={20} />
                  ) : (
                    user && (
                      <Typography
                        sx={{
                          fontWeight: 300,
                          fontSize: 14,
                          maxWidth: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Tooltip title="Org Unit">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color:
                                theme.palette.mode === 'light'
                                  ? theme.palette.grey[700]
                                  : theme.palette.grey[400],
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Groups fontSize="inherit" sx={{ mr: 1 }} />
                            {createOU(user)}
                          </Box>
                        </Tooltip>
                      </Typography>
                    )
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <Grid>
            <Stack direction="row" display="flex" alignItems="center">
              <Tooltip
                arrow
                title={
                  currentTier === 'essentials'
                    ? `At least Starter Tier is required to access this tool.`
                    : 'Reports'
                }
              >
                <span>
                  <IconButton
                    id="user-reports"
                    aria-controls={open ? 'reports-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    disabled={
                      toolsRoles.length === 0 ||
                      !user ||
                      !domains ||
                      currentTier === 'essentials'
                    }
                  >
                    <Assessment />
                  </IconButton>
                </span>
              </Tooltip>
              <UserReportsMenu
                anchorEl={anchorEl}
                open={open}
                handleClose={handleClose}
                setOpenReportsDialog={setOpenReportsDialog}
                setSelectedReport={setSelectedReport}
                toolsRoles={toolsRoles}
              />

              <Tooltip arrow title="Delete">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDeleteUser(true);
                    }}
                    disabled={
                      !isAbleToSeeUIElement(
                        userRoles,
                        UserRoles.DeleteUsers.value
                      ) ||
                      !user ||
                      !userEmail
                    }
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip arrow title={detailsExpanded ? 'Collapse' : 'Expand'}>
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailsExpanded((prev) => !prev);
                    }}
                  >
                    <ExpandMore
                      sx={{
                        transform: detailsExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        <Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
          <Grid
            container
            spacing={3}
            alignItems={'center'}
            sx={{
              py: 1,
              px: 4,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {user?.id && (
              <Grid>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.secondary',
                  }}
                >
                  Google ID:
                </Typography>
                <Box sx={{ minHeight: '26px' }}>
                  <TextHoverCopy
                    clipboardValue={user.id}
                    value={user.id}
                    valueType="Google ID"
                    textSx={{ color: 'text.secondary' }}
                    stackSx={{ pl: 0 }}
                    iconSx={{ fontSize: 14 }}
                  />
                </Box>
              </Grid>
            )}
            <Grid>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}
              >
                <CalendarToday
                  fontSize="inherit"
                  sx={{
                    verticalAlign: 'text-top',
                  }}
                />{' '}
                Created:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {user?.accountCreatedTime
                  ? new Date(user.accountCreatedTime).toLocaleString()
                  : ''}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                }}
              >
                <AccessTime
                  fontSize="inherit"
                  sx={{
                    verticalAlign: 'text-top',
                  }}
                />{' '}
                Last Sign In:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {user?.lastLoginTime
                  ? new Date(user.lastLoginTime).toLocaleString()
                  : 'Never'}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}
              >
                <Security
                  fontSize="inherit"
                  sx={{
                    verticalAlign: 'text-top',
                  }}
                />{' '}
                2-Step Authentication:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {user?.isEnrolledIn2Sv ? 'Enabled' : 'Disabled'}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', color: 'text.secondary' }}
              >
                <Storage
                  fontSize="inherit"
                  sx={{
                    verticalAlign: 'text-top',
                  }}
                />{' '}
                Drive Storage:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {isUserStorageLoading || isUserLoading ? (
                  <Skeleton width={115} height={60} variant="text" />
                ) : storageError?.message === 'DriveStorageUnavailable' ? (
                  'Unavailable'
                ) : storageError ? (
                  '-'
                ) : (
                  `${convertBytes(
                    userWithStorageDetails?.driveUsage || 0
                  )} / ${convertBytes(
                    userWithStorageDetails?.totalStorage || 0
                  )}`
                )}
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      </Stack>

      {domains ? (
        <DeleteUserDialog
          open={openDeleteUser}
          handleClose={() => setOpenDeleteUser(false)}
          userEmail={userEmail}
          domains={domains}
          changeLogRoles={changeLogRoles}
          primaryDomain={primaryDomain}
        />
      ) : null}
      {user ? (
        uiEnhancementFeatureEnabled ? (
          <EditProfilePicDialog
            open={openEditPictureDialog}
            handleClose={() => setOpenEditPictureDialog(false)}
            userEmail={userEmail}
            user={user}
            changeLogRoles={changeLogRoles}
          />
        ) : (
          <EditProfilePicDialogOld
            open={openEditPictureDialog}
            handleClose={() => setOpenEditPictureDialog(false)}
            userEmail={userEmail}
            user={user}
            changeLogRoles={changeLogRoles}
          />
        )
      ) : null}
      {user && userDecomStatus ? (
        <PauseUserDecomDialog
          open={openPauseUserDecomDialog}
          handleClose={() => setOpenPauseUserDecomDialog(false)}
          userDecomStatus={userDecomStatus}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {user && userDecomStatus ? (
        <UnpauseUserDecomDialog
          open={openUnpauseUserDecomDialog}
          handleClose={() => setOpenUnpauseUserDecomDialog(false)}
          userDecomStatus={userDecomStatus}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {user && userDecomStatus ? (
        <TerminateUserDecomDialog
          open={openTerminateUserDecomDialog}
          handleClose={() => setOpenTerminateUserDecomDialog(false)}
          userDecomStatus={userDecomStatus}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {user && userDecomStatus ? (
        <PurgeUserDecomDialog
          open={openPurgeUserDecomDialog}
          handleClose={() => setOpenPurgeUserDecomDialog(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      <ReportsDialog
        open={openReportsDialog}
        handleClose={() => setOpenReportsDialog(false)}
        selectedReport={selectedReport}
        sourceEmails={[userEmail]}
        sourceType={'User'}
        changeLogRoles={changeLogRoles}
      />
    </>
  );
}
